import React from 'react';

import { useMachine } from '@xstate/react';

// eslint-disable-next-line import/order
import {
	createAIExperienceMachine,
	aiExperienceActorStore,
} from '../utils/xstate/get-ai-experience-service';
// eslint-disable-next-line import/order
import type {
	AIExperienceMachineContext,
	AIExperienceMachineState,
	ContextStatistics,
	ExperienceMachineOptions,
	Positions,
} from '../utils/xstate/get-ai-experience-service';
import type { DocNode } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
// eslint-disable-next-line import/order
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { ConvoAIResponseRovoAction, PluginFeedbackMetadata } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import type { FAILURE_REASON } from '../utils/errors';

export function useAIExperienceService(options: ExperienceMachineOptions): {
	// In a later version of xstate -- the type gen means the machine
	// has good types, so these assertions are not required
	state: AIExperienceMachineState;
	context: AIExperienceMachineContext;
	send: (
		event:
			| {
					context: {
						configItem: EditorPluginAIConfigItem;
						parentPresetPromptLabel?: string;
						userADFInput?: JSONDocNode;
						userInput: string;
					};
					type: 'kickoff with command palette input';
			  }
			| {
					adfResponse?: DocNode;
					loadingStatus?: string;
					markdown: string;
					type: 'stream';
			  }
			| {
					adfResponse?: DocNode;
					cacheKey: string;
					inputOutputDiffRatio?: string;
					markdown: string;
					modelInput: {
						doc?: string;
						selection?: string;
					};
					rovoActions?: ConvoAIResponseRovoAction[];
					type: 'complete';
			  }
			| {
					cacheKey?: string;
					errorInfo: {
						apiName?: string;
						errorContent?: string;
						errorKey?: string;
						failureReason: FAILURE_REASON;
						statusCode?: number;
					};
					modelInput?: {
						doc?: string;
						selection?: string;
					};
					retryAfter?: number;
					type: 'error';
			  }
			| {
					type: 'retry';
			  }
			| {
					latestPromptResponse: string;
					type: 'interrogate';
					userADFInput?: JSONDocNode;
					userInput: string;
			  }
			| {
					configItem: EditorPluginAIConfigItem;
					latestPromptResponse: string;
					type: 'refine';
			  }
			| {
					type: 'kick off';
			  }
			| {
					type: 'same cache key';
			  }
			| {
					positions: Positions;
					type: 'update positions';
			  }
			| {
					input: string;
					type: 'update user input';
			  }
			| {
					adf?: JSONDocNode;
					type: 'update user input adf';
			  }
			| {
					adf: JSONDocNode | undefined;
					input: string | undefined;
					type: 'retry prompt';
			  }
			| {
					presetTitle?: string;
					type: 'edit prompt';
					userADFInput?: JSONDocNode;
					userInput?: string;
			  }
			| {
					context: {
						configItem: EditorPluginAIConfigItem;
						userADFInput?: JSONDocNode;
						userInput: string;
					};
					type: 'update context';
			  }
			| {
					context: {
						idMap: IDMap;
					};
					type: 'update idMap';
			  }
			| {
					context: {
						traceIds: string[];
					};
					type: 'update traceIds';
			  }
			| {
					context: {
						selectionMetaData: {
							sliceOpenEnd?: number;
							sliceOpenStart?: number;
						};
					};
					type: 'update selectionMetaData';
			  }
			| {
					context: {
						adfResponseNodeWrappers: string[];
					};
					type: 'update adfResponseNodeWrappers';
			  }
			| {
					type: 'start refinement';
			  }
			| {
					type: 'cancel refinement';
			  }
			| {
					context?: {
						onceDiscard?: () => unknown;
					};
					type: 'enable discard';
			  }
			| {
					type: 'disable discard';
			  }
			| {
					type: 'escape';
			  }
			| {
					contextStatistics: ContextStatistics;
					type: 'storeContextStatistics';
			  }
			| {
					adfResponse?: DocNode;
					cacheKey: string;
					inputOutputDiffRatio?: string;
					modelInput: {
						doc?: string;
						selection?: string;
					};
					type: 'response too similar';
			  }
			| {
					adfResponse: DocNode;
					type: 'retry on adf stream errors';
			  }
			| {
					sentiment: PluginFeedbackMetadata['sentiment'];
					type: 'feedback submitted';
			  }
			| {
					type: 'toggle use page knowledge';
			  }
			| {
					baseGenerate: EditorPluginAIConfigItem;
					configItem: EditorPluginAIConfigItem;
					type: 'update baseGenerate and configItem';
			  }
			| {
					type: 'reset';
			  }
			| {
					context: AIExperienceMachineContext;
					type: 'initialise context';
			  }
			| {
					type: 'startStreamingForCreate';
			  },
	) => void;
} {
	const machineRef = React.useRef<ReturnType<typeof createAIExperienceMachine> | null>(null);

	if (machineRef.current === null) {
		machineRef.current = createAIExperienceMachine(options);
	}

	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const [state, send, actor] = useMachine(machineRef.current!);

	aiExperienceActorStore.setActor(options.context.editorView, actor);

	return {
		// In a later version of xstate -- the type gen means the machine
		// has good types, so these assertions are not required
		state: state.value as AIExperienceMachineState,
		context: state.context,
		send,
	};
}
